import { useState, useRef, useCallback, useEffect } from 'react';

export type SaveButtonStatus = 'ready' | 'saving' | 'saved-pending' | 'saved-confirmed';

interface UseSaveButtonStatusOptions {
    confirmationDurationMs?: number;
    maxPendingMs?: number;
    maxSavingMs?: number;
}

interface UseSaveButtonStatusReturn {
    status: SaveButtonStatus;
    markSaving: () => string;
    markSaved: (saveId: string) => void;
    reset: () => void;
}

export const useSaveButtonStatus = (options: UseSaveButtonStatusOptions = {}): UseSaveButtonStatusReturn => {
    const { confirmationDurationMs = 3000, maxPendingMs = 30000, maxSavingMs = 10000 } = options;

    const [status, setStatus] = useState<SaveButtonStatus>('ready');
    const saveIdRef = useRef<string | null>(null);
    const savingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const confirmationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearAllTimers = useCallback(() => {
        if (savingTimeoutRef.current !== null) {
            clearTimeout(savingTimeoutRef.current);
            savingTimeoutRef.current = null;
        }
        if (pendingWatchdogRef.current !== null) {
            clearTimeout(pendingWatchdogRef.current);
            pendingWatchdogRef.current = null;
        }
        if (confirmationTimeoutRef.current !== null) {
            clearTimeout(confirmationTimeoutRef.current);
            confirmationTimeoutRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        clearAllTimers();
        saveIdRef.current = null;
        setStatus('ready');
    }, [clearAllTimers]);

    const markSaving = useCallback((): string => {
        clearAllTimers();
        const saveId = crypto.randomUUID();
        saveIdRef.current = saveId;
        setStatus('saving');

        savingTimeoutRef.current = setTimeout(() => {
            if (saveIdRef.current === saveId) {
                saveIdRef.current = null;
                setStatus('ready');
            }
        }, maxSavingMs);

        return saveId;
    }, [clearAllTimers, maxSavingMs]);

    useEffect(() => {
        const handleApiFired = (event: Event) => {
            const detail = (event as CustomEvent<{ saveId: string }>).detail;
            if (!detail?.saveId || detail.saveId !== saveIdRef.current) return;

            if (savingTimeoutRef.current !== null) {
                clearTimeout(savingTimeoutRef.current);
                savingTimeoutRef.current = null;
            }

            setStatus('saved-pending');

            pendingWatchdogRef.current = setTimeout(() => {
                if (saveIdRef.current === detail.saveId) {
                    saveIdRef.current = null;
                    setStatus('ready');
                }
            }, maxPendingMs);
        };

        const handleSaveAborted = (event: Event) => {
            const detail = (event as CustomEvent<{ saveId: string }>).detail;
            if (!detail?.saveId || detail.saveId !== saveIdRef.current) return;

            clearAllTimers();
            saveIdRef.current = null;
            setStatus('ready');
        };

        const handleSwMessage = (event: MessageEvent) => {
            const { type, saveId } = (event.data ?? {}) as { type?: string; saveId?: string };
            if (!saveId || saveId !== saveIdRef.current) return;

            if (type === 'BACKGROUND_SAVE_COMPLETED') {
                clearAllTimers();
                saveIdRef.current = null;
                setStatus('saved-confirmed');
                confirmationTimeoutRef.current = setTimeout(() => {
                    setStatus('ready');
                }, confirmationDurationMs);
            } else if (type === 'BACKGROUND_SAVE_FAILED') {
                clearAllTimers();
                saveIdRef.current = null;
                setStatus('ready');
            }
        };

        window.addEventListener('save-api-fired', handleApiFired);
        window.addEventListener('save-aborted', handleSaveAborted);

        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', handleSwMessage);
        }

        return () => {
            window.removeEventListener('save-api-fired', handleApiFired);
            window.removeEventListener('save-aborted', handleSaveAborted);
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', handleSwMessage);
            }
            clearAllTimers();
        };
    }, [clearAllTimers, confirmationDurationMs, maxPendingMs]);

    const markSaved = useCallback((saveId: string): void => {
        if (saveIdRef.current !== saveId) return;
        clearAllTimers();
        saveIdRef.current = null;
        setStatus('saved-confirmed');
        confirmationTimeoutRef.current = setTimeout(() => {
            setStatus('ready');
        }, confirmationDurationMs);
    }, [clearAllTimers, confirmationDurationMs]);

    return { status, markSaving, markSaved, reset };
};
